import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserOwnTractorFilterOptionValueRegionEnum,
  UserOwnTractorFilterOptionValueSorterEnum,
} from '../../constants/filter/userOwnTractorFilter.constant';
import { filterSlice } from '../../store/filterSlice';
import { useAppDispatch, useAppSelector } from '../store/useStore';

export const useUpdateUserOwnTractorFilterStates = () => {
  const dispatch = useAppDispatch();
  const userOwnTractorFilterStates = useAppSelector((state) => state.filter.userOwnTractorFilter);
  const filterActions = filterSlice.actions;

  const [searchParams] = useSearchParams();

  const sortSearchParam = useMemo<string>(() => {
    if (searchParams.has('sort')) {
      return searchParams.get('sort')!;
    }
    return '';
  }, [searchParams]);

  const brandSearchParam = useMemo<string>(() => {
    if (searchParams.has('brand')) {
      return searchParams.get('brand')!;
    }
    return '';
  }, [searchParams]);

  const regionDepth1SearchParam = useMemo<string>(() => {
    if (searchParams.has('regionDepth1')) {
      return searchParams.get('regionDepth1')!;
    }
    return '';
  }, [searchParams]);

  const minEngineOutputPsSearchParam = useMemo<string>(() => {
    if (searchParams.has('minEngineOutputPs')) {
      return searchParams.get('minEngineOutputPs')!;
    }
    return '';
  }, [searchParams]);

  const maxEngineOutputPsSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxEngineOutputPs')) {
      return searchParams.get('maxEngineOutputPs')!;
    }
    return '';
  }, [searchParams]);

  useEffect(() => {
    // search param 체크가 아직 안되었고, 필터 옵션에 brands가 모두 추가되었을 때 수행
    if (!userOwnTractorFilterStates.isSearchParamChecked && userOwnTractorFilterStates.isFilterOptionsAdded.brands) {
      dispatch(filterActions.resetAllFilters({ filterType: 'userOwnTractorFilter' }));

      // searchParams 이 있으면 수행
      if (searchParams) {
        // sortSearchParam 이 있는 경우
        if (sortSearchParam) {
          Object.values(UserOwnTractorFilterOptionValueSorterEnum).forEach((sortOptionValue) => {
            if (sortSearchParam.includes(sortOptionValue)) {
              const targetFilter = userOwnTractorFilterStates.globalFilters.find((filter) => filter.key === 'sorter');
              const targetOption = targetFilter?.options.find((option) => option.value === sortOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'userOwnTractorFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // regionDepth1SearchParam 이 있는 경우
        if (regionDepth1SearchParam) {
          Object.values(UserOwnTractorFilterOptionValueRegionEnum).forEach((regionDepth1OptionValues) => {
            if (regionDepth1SearchParam.includes(regionDepth1OptionValues as string)) {
              const targetFilter = userOwnTractorFilterStates.globalFilters.find(
                (filter) => filter.key === 'filter_region',
              );
              const targetOption = targetFilter?.options.find((option) => option.value === regionDepth1OptionValues);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'userOwnTractorFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // brandSearchParam 이 있는 경우
        if (brandSearchParam) {
          brandSearchParam.split(',').forEach((brandOptionValue) => {
            if (brandSearchParam.includes(brandOptionValue)) {
              const targetFilter = userOwnTractorFilterStates.globalFilters.find(
                (filter) => filter.key === 'filter_brand',
              );
              const targetOption = targetFilter?.options.find((option) => option.value === brandOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'userOwnTractorFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        if (minEngineOutputPsSearchParam && maxEngineOutputPsSearchParam) {
          const targetFilter = userOwnTractorFilterStates.globalFilters.find(
            (filter) => filter.key === 'engineOutputInPs',
          );

          if (targetFilter) {
            dispatch(
              filterActions.changeFilterOptionValue({
                filterType: 'userOwnTractorFilter',
                targetFilter,
                optionValues: [Number(minEngineOutputPsSearchParam), Number(maxEngineOutputPsSearchParam)],
              }),
            );
          }
        }
      }

      dispatch(filterActions.changeIsSearchParamChecked({ filterType: 'userOwnTractorFilter', value: true }));
    }
  }, [
    dispatch,
    filterActions,
    userOwnTractorFilterStates.globalFilters,
    userOwnTractorFilterStates.isSearchParamChecked,
    userOwnTractorFilterStates.isFilterOptionsAdded.brands,
    searchParams,
    brandSearchParam,
    regionDepth1SearchParam,
    sortSearchParam,
    minEngineOutputPsSearchParam,
    maxEngineOutputPsSearchParam,
  ]);
};
